/**
 * Map Data API Service
 */

import apiClient from '../client';
import API_CONFIG from '../../config/api.config';
import { log, LogCategory } from '../../lib/logger';
import type {
  APIResponse,
  CreateProjectDataResponse,
  GetProjectDataResponse,
  UpdateProjectDataResponse,
  MapData,
} from '../types';

const { ENDPOINTS } = API_CONFIG;

/**
 * Map Data API
 */
export const mapDataService = {
  /**
   * 맵 데이터 생성
   * @param projectId - 프로젝트 ID
   * @param mapData - 맵 데이터 객체 (자동으로 JSON.stringify 처리)
   * @param templateId - 템플릿 ID (선택, mapData.template_id가 우선)
   * @returns 생성된 맵 데이터 정보
   */
  async createMapData(
    projectId: number,
    mapData: MapData,
    templateId?: number
  ): Promise<APIResponse<CreateProjectDataResponse>> {
    // mapData에 template_id가 있으면 우선 사용, 없으면 파라미터 사용
    const finalTemplateId = mapData.template_id ?? templateId;

    // Create FormData for multipart/form-data request (better for large JSON)
    const formData = new FormData();
    formData.append('project_id', projectId.toString());
    formData.append('map_data', JSON.stringify(mapData));
    if (finalTemplateId !== undefined) {
      formData.append('template_id', finalTemplateId.toString());
    }

    return apiClient.post<CreateProjectDataResponse>(
      ENDPOINTS.MAP_DATA.PROJECT_DATA,
      formData
      // Note: Don't set Content-Type header manually
      // Axios will automatically set it with the correct boundary parameter
    );
  },

  /**
   * 맵 데이터 조회
   * @param projectId - 프로젝트 ID
   * @returns 맵 데이터 목록 (자동으로 JSON.parse 처리)
   */
  async getMapData(projectId: number): Promise<APIResponse<GetProjectDataResponse>> {
    const response = await apiClient.get<GetProjectDataResponse>(
      ENDPOINTS.MAP_DATA.PROJECT_DATA_BY_ID(projectId)
    );

    // map_data를 JSON 객체로 파싱
    if (response.success && response.data) {
      response.data = response.data.map((item) => ({
        ...item,
        map_data: item.map_data, // 문자열 그대로 유지 (파싱은 사용처에서)
      }));
    }

    return response;
  },

  /**
   * 맵 데이터 조회 (파싱된 객체로 반환)
   * @param projectId - 프로젝트 ID
   * @returns 파싱된 맵 데이터 목록
   */
  async getMapDataParsed(
    projectId: number
  ): Promise<
    APIResponse<
      Array<
        Omit<GetProjectDataResponse[number], 'map_data'> & { map_data: MapData }
      >
    >
  > {
    const response = await this.getMapData(projectId);

    if (response.success && response.data) {
      const parsedData = response.data.map((item) => {
        try {
          return { ...item, map_data: JSON.parse(item.map_data) as MapData };
        } catch {
          console.error(`[mapDataService] Failed to parse map_data for floor ${item.id}`);
          return { ...item, map_data: null as unknown as MapData };
        }
      });

      return {
        ...response,
        data: parsedData,
      };
    }

    return response as never;
  },

  /**
   * 맵 데이터 수정
   * @param projectId - 프로젝트 ID
   * @param mapData - 맵 데이터 객체 (자동으로 JSON.stringify 처리)
   * @returns 수정된 맵 데이터 정보
   */
  async updateMapData(
    projectId: number,
    mapData: MapData
  ): Promise<APIResponse<UpdateProjectDataResponse>> {
    // Create FormData for multipart/form-data request (better for large JSON)
    const formData = new FormData();
    formData.append('project_id', projectId.toString());
    formData.append('map_data', JSON.stringify(mapData));

    // Debug: Log FormData contents
    log.debug(LogCategory.API, 'Sending FormData', {
      projectId,
      mapDataLength: JSON.stringify(mapData).length
    });

    return apiClient.put<UpdateProjectDataResponse>(
      ENDPOINTS.MAP_DATA.PROJECT_DATA,
      formData
      // Note: Don't set Content-Type header manually
      // Browser will automatically set it with the correct boundary parameter
    );
  },

  /**
   * 자동 저장 (Debounced)
   * 주의: 이 함수는 직접 사용하지 말고 debounce wrapper를 통해 사용하세요
   * @param projectId - 프로젝트 ID
   * @param mapData - 맵 데이터 객체
   * @returns 수정된 맵 데이터 정보
   */
  async autoSave(
    projectId: number,
    mapData: MapData
  ): Promise<APIResponse<UpdateProjectDataResponse>> {
    return this.updateMapData(projectId, mapData);
  },
};

export default mapDataService;
